/**
 * Riepiloghi speso/posti per rosa avversaria (e Da assegnare).
 */

import { opponentPlaceholder } from './auctionOpponents.js';
import { ROSE_ALTRI_UNASSIGNED_ID, isUnassignedOpponentId } from './roseAltriRoster.js';

const MIN_BID = 1;

/**
 * Massima offerta sul prossimo giocatore rispettando rosa piena e minimo 1M per slot restante.
 */
export function computeMaxBid({ budget, spent, owned, maxPlayers, minPrice = MIN_BID } = {}) {
  const totalBudget = Math.max(0, Number(budget) || 0);
  const totalSpent = Math.max(0, Number(spent) || 0);
  const ownedCount = Math.max(0, Number(owned) || 0);
  const slots = Math.max(0, Number(maxPlayers) || 0);
  const residual = Math.max(0, totalBudget - totalSpent);
  const slotsLeft = Math.max(0, slots - ownedCount);
  if (slotsLeft <= 0) return 0;
  const reserve = Math.max(0, slotsLeft - 1) * Math.max(1, Number(minPrice) || MIN_BID);
  return Math.max(0, residual - reserve);
}

/**
 * Livello «batteria» crediti: percentuale residua e fascia colore.
 */
export function creditBatteryLevel({ budget, spent } = {}) {
  const total = Math.max(0, Number(budget) || 0);
  const used = Math.max(0, Math.min(total, Number(spent) || 0));
  const remaining = Math.max(0, total - used);
  if (total <= 0) {
    return { pct: 0, level: 'low', remaining: 0, total: 0 };
  }
  const pct = Math.round((remaining / total) * 100);
  let level = 'high';
  if (pct <= 20) level = 'low';
  else if (pct <= 45) level = 'mid';
  return { pct, level, remaining, total };
}

/**
 * Benchmark «più ricco»: percentuale residua del partecipante con più crediti.
 */
export function richestCreditBenchmark(rows) {
  if (!Array.isArray(rows) || !rows.length) {
    return { pct: 0, remaining: 0 };
  }
  let maxRemaining = 0;
  let maxBudget = 0;
  for (const row of rows) {
    const { remaining, total } = creditBatteryLevel(row);
    if (remaining > maxRemaining) {
      maxRemaining = remaining;
      maxBudget = total;
    }
  }
  const pct = maxBudget > 0 ? Math.round((maxRemaining / maxBudget) * 100) : 0;
  return { pct, remaining: maxRemaining };
}

/**
 * Ordina righe Per rosa: max offerta decrescente, poi residuo, poi nome.
 */
export function sortPerRosaRows(rows) {
  return [...rows].sort((a, b) => {
    const maxDiff = (b.maxBid ?? 0) - (a.maxBid ?? 0);
    if (maxDiff !== 0) return maxDiff;
    const resA = (a.budget ?? 0) - (a.spent ?? 0);
    const resB = (b.budget ?? 0) - (b.spent ?? 0);
    if (resB !== resA) return resB - resA;
    return String(a.name || '').localeCompare(String(b.name || ''), 'it');
  });
}

/**
 * @param {Map<string, { price?: number, opponentId?: string|null }>|null|undefined} auctionExits
 * @param {{ id: string, name: string }[]|null|undefined} opponents
 * @param {{ budget?: number, maxPlayers?: number }} [opts]
 * @returns {{ id: string, name: string, spent: number, owned: number, budget: number, maxPlayers: number, avg: number|null, pile?: boolean }[]}
 */
export function buildOpponentSpendRows(auctionExits, opponents, opts = {}) {
  const budget = Math.max(0, Number(opts.budget) || 0);
  const maxPlayers = Math.max(0, Number(opts.maxPlayers) || 0);
  const list = Array.isArray(opponents) ? opponents : [];

  const rows = list.map((o, index) => {
    const id = String(o?.id || '').trim();
    const rawName = String(o?.name ?? '').trim();
    return {
      id,
      name: rawName || opponentPlaceholder(index),
      spent: 0,
      owned: 0,
      budget,
      maxPlayers,
      avg: null,
      pile: false,
    };
  }).filter((r) => r.id);

  const unassigned = {
    id: ROSE_ALTRI_UNASSIGNED_ID,
    name: 'Da assegnare',
    spent: 0,
    owned: 0,
    budget,
    maxPlayers,
    avg: null,
    pile: true,
  };

  const byId = new Map(rows.map((r) => [r.id, r]));

  if (auctionExits instanceof Map) {
    for (const entry of auctionExits.values()) {
      const price = Number(entry?.price);
      if (!Number.isFinite(price) || price < 1) continue;
      if (isUnassignedOpponentId(entry?.opponentId)) {
        unassigned.spent += price;
        unassigned.owned += 1;
        continue;
      }
      const row = byId.get(String(entry.opponentId));
      if (!row) {
        // Id orfano: conta in Da assegnare
        unassigned.spent += price;
        unassigned.owned += 1;
        continue;
      }
      row.spent += price;
      row.owned += 1;
    }
  }

  const finalize = (row) => {
    row.avg = row.owned > 0 ? Math.round(row.spent / row.owned) : null;
    row.maxBid = computeMaxBid({
      budget: row.budget,
      spent: row.spent,
      owned: row.owned,
      maxPlayers: row.maxPlayers,
    });
    return row;
  };

  const out = rows.map(finalize);
  if (unassigned.owned > 0 || unassigned.spent > 0) {
    out.push(finalize(unassigned));
  }
  return out;
}

/**
 * Riga «io» (Rosa propria) per Analytics.
 */
export function buildOwnSpendRow(roster, opts = {}) {
  const budget = Math.max(0, Number(opts.budget) || 0);
  const maxPlayers = Math.max(0, Number(opts.maxPlayers) || 0);
  let spent = 0;
  let owned = 0;
  if (roster instanceof Map) {
    for (const entry of roster.values()) {
      const price = Number(entry?.price);
      if (!Number.isFinite(price) || price < 1) continue;
      spent += price;
      owned += 1;
    }
  }
  return {
    id: 'me',
    name: 'Tu',
    spent,
    owned,
    budget,
    maxPlayers,
    avg: owned > 0 ? Math.round(spent / owned) : null,
    pile: false,
    maxBid: computeMaxBid({ budget, spent, owned, maxPlayers }),
  };
}
